import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchBillingConfig,
  fetchBillingPayments,
  fetchBillingStatus,
  fetchBillingTokenStatus,
  createBillingToken,
  processBillingPayment,
  updateBillingConfig,
  fetchBillingCards,
  createBillingCard,
  setDefaultBillingCard,
  deleteBillingCard,
  fetchBillingDocuments,
  uploadBillingDocument,
  fetchBillingKycStatus,
} from "./thunks";
import type {
  EstablishmentBillingConfig,
  BillingPaymentsResponse,
  BillingStatus,
  BillingCard,
  BillingTokenStatus,
} from "@/types/billing";
import type { AsaasDocumentRequirement, AsaasKycStatus } from "@/types";
import { toast } from "react-toastify";

interface EstablishmentBillingState {
  config: EstablishmentBillingConfig | null;
  payments: BillingPaymentsResponse | null;
  status: BillingStatus | null;
  tokenStatus: BillingTokenStatus | null;
  documents: AsaasDocumentRequirement[];
  documentsRejectReasons: Record<string, unknown> | null;
  kycStatus: AsaasKycStatus | null;

  loadingConfig: boolean;
  loadingPayments: boolean;
  loadingStatus: boolean;
  loadingTokenStatus: boolean;
  loadingDocuments: boolean;
  uploadingDocument: boolean;
  loadingKycStatus: boolean;
  loadingProcess: boolean;
  loadingUpdate: boolean;
  creatingToken: boolean;

  // Cards
  cards: BillingCard[];
  loadingCards: boolean;
  loadingCreateCard: boolean;
  loadingSetDefaultCard: boolean;
  loadingDeleteCard: boolean;

  error: string | null;
}

const initialState: EstablishmentBillingState = {
  config: null,
  payments: null,
  status: null,
  tokenStatus: null,
  documents: [],
  documentsRejectReasons: null,
  kycStatus: null,

  loadingConfig: false,
  loadingPayments: false,
  loadingStatus: false,
  loadingTokenStatus: false,
  loadingDocuments: false,
  uploadingDocument: false,
  loadingKycStatus: false,
  loadingProcess: false,
  loadingUpdate: false,
  creatingToken: false,

  cards: [],
  loadingCards: false,
  loadingCreateCard: false,
  loadingSetDefaultCard: false,
  loadingDeleteCard: false,

  error: null,
};

const establishmentBillingSlice = createSlice({
  name: "establishmentBilling",
  initialState,
  reducers: {
    resetBillingState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Config
      .addCase(fetchBillingConfig.pending, (state) => {
        state.loadingConfig = true;
        state.error = null;
      })
      .addCase(
        fetchBillingConfig.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingConfig = false;
          state.config = action.payload.data;
        },
      )
      .addCase(
        fetchBillingConfig.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingConfig = false;
          state.error =
            action.payload?.message || "Erro ao buscar configuração de billing";
        },
      )

      // Payments
      .addCase(fetchBillingPayments.pending, (state) => {
        state.loadingPayments = true;
        state.error = null;
      })
      .addCase(
        fetchBillingPayments.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingPayments = false;
          state.payments = action.payload.data;
        },
      )
      .addCase(
        fetchBillingPayments.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingPayments = false;
          state.error = action.payload?.message || "Erro ao buscar pagamentos";
        },
      )

      // Status
      .addCase(fetchBillingStatus.pending, (state) => {
        state.loadingStatus = true;
        state.error = null;
      })
      .addCase(
        fetchBillingStatus.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingStatus = false;
          state.status = action.payload.data;
        },
      )
      .addCase(
        fetchBillingStatus.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingStatus = false;
          state.error =
            action.payload?.message || "Erro ao buscar status de billing";
        },
      )

      // Token creation
      .addCase(createBillingToken.pending, (state) => {
        state.creatingToken = true;
        state.error = null;
      })
      .addCase(
        createBillingToken.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.creatingToken = false;
          state.tokenStatus = action.payload?.data ?? state.tokenStatus;
          const message = action.payload?.message;
          toast.success(message || "Conta bancária criada com sucesso");
        },
      )
      .addCase(
        createBillingToken.rejected,
        (state, action: PayloadAction<any>) => {
          state.creatingToken = false;
          const msg = action.payload?.message || "Erro ao criar conta bancária";
          state.error = msg;
          toast.error(msg);
        },
      )

      // Token status
      .addCase(fetchBillingTokenStatus.pending, (state) => {
        state.loadingTokenStatus = true;
        state.error = null;
      })
      .addCase(
        fetchBillingTokenStatus.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingTokenStatus = false;
          state.tokenStatus = action.payload.data;
        },
      )
      .addCase(
        fetchBillingTokenStatus.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingTokenStatus = false;
          state.error =
            action.payload?.message ||
            "Erro ao buscar status do token de billing";
        },
      )

      // Process payment
      .addCase(processBillingPayment.pending, (state) => {
        state.loadingProcess = true;
        state.error = null;
      })
      .addCase(
        processBillingPayment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingProcess = false;
          toast.success(
            action.payload?.message || "Pagamento processado com sucesso",
          );
        },
      )
      .addCase(
        processBillingPayment.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingProcess = false;
          const msg = action.payload?.message || "Erro ao processar pagamento";
          state.error = msg;
          toast.error(msg);
        },
      )

      // Update config
      .addCase(updateBillingConfig.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
      })
      .addCase(
        updateBillingConfig.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingUpdate = false;
          // Some APIs return only message; refresh is handled by caller, but keep local if provided
          if (action.payload?.data) {
            state.config = action.payload.data;
          }
          toast.success(
            action.payload?.message || "Configuração atualizada com sucesso",
          );
        },
      )
      .addCase(
        updateBillingConfig.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingUpdate = false;
          const msg =
            action.payload?.message || "Erro ao atualizar configuração";
          state.error = msg;
          toast.error(msg);
        },
      )

      // Cards
      .addCase(fetchBillingCards.pending, (state) => {
        state.loadingCards = true;
        state.error = null;
      })
      .addCase(
        fetchBillingCards.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingCards = false;
          state.cards = action.payload?.data || [];
        },
      )
      .addCase(
        fetchBillingCards.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingCards = false;
          const msg = action.payload?.message || "Erro ao buscar cartões";
          state.error = msg;
        },
      )

      .addCase(createBillingCard.pending, (state) => {
        state.loadingCreateCard = true;
        state.error = null;
      })
      .addCase(
        createBillingCard.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingCreateCard = false;
          const created = action.payload?.data as BillingCard | undefined;
          if (created) {
            if (created.is_default) {
              state.cards = state.cards.map((c) => ({
                ...c,
                is_default: false,
              }));
            }
            state.cards.unshift(created);
          }
          toast.success(
            action.payload?.message || "Cartão adicionado com sucesso",
          );
        },
      )
      .addCase(
        createBillingCard.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingCreateCard = false;
          const msg = action.payload?.message || "Erro ao adicionar cartão";
          state.error = msg;
          toast.error(msg);
        },
      )

      .addCase(setDefaultBillingCard.pending, (state) => {
        state.loadingSetDefaultCard = true;
        state.error = null;
      })
      .addCase(
        setDefaultBillingCard.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingSetDefaultCard = false;
          const updated = action.payload?.data as BillingCard | undefined;
          if (updated) {
            state.cards = state.cards.map((c) => ({
              ...c,
              is_default: c.id === updated.id,
            }));
          }
          toast.success(
            action.payload?.message || "Cartão definido como padrão",
          );
        },
      )
      .addCase(
        setDefaultBillingCard.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingSetDefaultCard = false;
          const msg =
            action.payload?.message || "Erro ao definir cartão padrão";
          state.error = msg;
          toast.error(msg);
        },
      )

      .addCase(deleteBillingCard.pending, (state) => {
        state.loadingDeleteCard = true;
        state.error = null;
      })
      .addCase(
        deleteBillingCard.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingDeleteCard = false;
          const removedId = action.payload?.data?.id as number | undefined;
          if (removedId) {
            state.cards = state.cards.filter((c) => c.id !== removedId);
          }
          toast.success(
            action.payload?.message || "Cartão removido com sucesso",
          );
        },
      )
      .addCase(
        deleteBillingCard.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingDeleteCard = false;
          const msg = action.payload?.message || "Erro ao remover cartão";
          state.error = msg;
          toast.error(msg);
        },
      )

      // Documents
      .addCase(fetchBillingDocuments.pending, (state) => {
        state.loadingDocuments = true;
        state.error = null;
      })
      .addCase(
        fetchBillingDocuments.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingDocuments = false;
          state.documents = action.payload?.documents ?? [];
          state.documentsRejectReasons = action.payload?.rejectReasons ?? null;
        },
      )
      .addCase(
        fetchBillingDocuments.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingDocuments = false;
          const msg = action.payload?.message || "Erro ao carregar documentos";
          state.error = msg;
        },
      )

      .addCase(uploadBillingDocument.pending, (state) => {
        state.uploadingDocument = true;
        state.error = null;
      })
      .addCase(
        uploadBillingDocument.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.uploadingDocument = false;
          state.documents = action.payload?.documents ?? [];
          state.documentsRejectReasons = action.payload?.rejectReasons ?? null;
          toast.success("Documento enviado com sucesso");
        },
      )
      .addCase(
        uploadBillingDocument.rejected,
        (state, action: PayloadAction<any>) => {
          state.uploadingDocument = false;
          const msg = action.payload?.message || "Erro ao enviar documento";
          state.error = msg;
          toast.error(msg);
        },
      )

      .addCase(fetchBillingKycStatus.pending, (state) => {
        state.loadingKycStatus = true;
        state.error = null;
      })
      .addCase(
        fetchBillingKycStatus.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingKycStatus = false;
          state.kycStatus = action.payload ?? null;
        },
      )
      .addCase(
        fetchBillingKycStatus.rejected,
        (state, action: PayloadAction<any>) => {
          state.loadingKycStatus = false;
          const msg =
            action.payload?.message || "Erro ao consultar situação cadastral";
          state.error = msg;
          toast.error(msg);
        },
      );
  },
});

export const { resetBillingState } = establishmentBillingSlice.actions;
export default establishmentBillingSlice.reducer;
